import { FloatingFormItem, InputSearch } from '@/components/_common';
import CpfCnpjField from '@/components/_common/CpfCnpjField';
import CustomersSelectedModal from '@/components/_common/CustomersSelectedModal';
import InsurersSelectedModal from '@/components/_common/InsurersSelectedModal';
import SelectYesNo from '@/components/_common/SelectYesNo';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import {
  InvoiceGeneralFormValues,
  InvoiceNFSeFormValues,
  RecipientType,
} from '@/core/domain/invoice/invoice/Invoice.types';
import {
  InvoiceRecipientType,
  InvoiceTaxpayerType,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { useGetCustomerMainAddress } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';
import { CustomerReduced } from '@/core/domain/people/Customers/Customers.types';
import { useGetInsurerMainAddress } from '@/core/domain/people/InsurerAddress/InsurersAddress.hooks';
import { Insurer } from '@/core/domain/people/Insurers/Insurers.types';
import { useGetSupplierMainAddress } from '@/core/domain/people/SupplierAddress/SupplierAddress.hooks';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { Col, Form, FormInstance, Input, Select } from 'antd';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

interface Entity {
  id: number;
  name: string;
  cpfCnpj?: string;
  foreignDocument?: string;
  cnpj?: string;
  cpf?: string;
  fantasyName?: string;
  isPublicAgency?: boolean;
}

interface Address {
  street: string;
  number?: string;
  district: string;
  city: string;
}

interface Props {
  isEdit?: boolean;
  form?:
    | FormInstance<InvoiceGeneralFormValues>
    | FormInstance<InvoiceNFSeFormValues>;
  disabledFields?: boolean;
  invoiceTypeEdit?: InvoiceType;
}

const SelectRecipientType = ({
  form,
  disabledFields,
  invoiceTypeEdit,
  isEdit,
}: Props) => {
  const { isNfs, invoice } = useInvoiceFormContext();

  const formValues = Form.useWatch([], form);
  const isLondrinaCity =
    (isEdit ? invoice?.data?.cityId : formValues?.cityId) === 146;

  const intl = useIntl().formatMessage;

  const invoiceType = Form.useWatch('invoiceType', form);

  const isNFC =
    invoiceTypeEdit === InvoiceType.NFC || invoiceType === InvoiceType.NFC;

  const [modalCustomerVisible, modalCustomerActions] = useDisclosure();
  const [modalSupplierVisible, modalSupplierActions] = useDisclosure();
  const [modalInsurersVisible, modalInsurersActions] = useDisclosure();

  const customerMainAddress = useGetCustomerMainAddress();
  const supplierMainAddress = useGetSupplierMainAddress();
  const insurerMainAddress = useGetInsurerMainAddress();

  const customerReduced = useCustomerReduced();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  useEffect(() => {
    if (!isEdit) {
      if (!isNFC) {
        form?.setFieldsValue({
          recipient: {
            recipientType: InvoiceRecipientType.CUSTOMER,
          },
          taxpayerType: InvoiceTaxpayerType.NON_CONTRIBUTOR,
          finalConsumer: 'true',
        });
      } else {
        clearRecipient();
      }
    }
  }, [isNFC, isEdit]);

  const recipientTypeOptions = [
    {
      label: intl({ id: 'invoice.nfe.recipient.form.recipientType.customer' }),
      value: InvoiceRecipientType.CUSTOMER,
    },
    {
      label: intl({ id: 'invoice.nfe.recipient.form.recipientType.supplier' }),
      value: InvoiceRecipientType.SUPPLIER,
    },
    {
      label: intl({ id: 'invoice.nfe.recipient.form.recipientType.insurer' }),
      value: InvoiceRecipientType.INSURER,
    },
  ];

  async function setRecipientFields(
    entity: Entity,
    address?: Address,
    type?: string,
  ) {
    const fullAddress = `${address?.street ?? ''}, ${address?.number ?? ''} - ${
      address?.district ?? ''
    }`;

    form?.setFieldsValue({
      recipient: {
        cpfCnpj: entity.cpfCnpj || entity.cnpj || entity.cpf,
        foreignDocument: entity.foreignDocument,
        [`${type}`]: entity.name,
        [`${type}Id`]: entity.id,
        fantasyName: entity.fantasyName,
        cityName: address?.city,
        customerAddress: address ? fullAddress : undefined,
        publicAgency:
          typeof entity?.isPublicAgency === 'boolean'
            ? String(entity.isPublicAgency)
            : undefined,
      },
    });
  }

  const handleEntitySelect = async (
    entity: Entity,
    fetchAddress: (id: number) => Promise<Address>,
    modalClose: () => void,
    type: string,
  ) => {
    let address: Address | undefined = undefined;

    try {
      if (entity) {
        address = await fetchAddress(entity.id);
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        address = undefined;
      } else {
        throw error;
      }
    } finally {
      await setRecipientFields(entity, address, type);
      await form?.validateFields();
      modalClose();
    }
  };

  const handlerCustomerSelect = async (customer: CustomerReduced) => {
    await handleEntitySelect(
      customer,
      customerMainAddress.mutateAsync,
      modalCustomerActions.close,
      'customer',
    );

    const response = await customerReduced.mutateAsync(customer.id);

    if (response) {
      form?.setFieldValue(
        ['recipient', 'publicAgency'],
        typeof response.isPublicAgency === 'boolean'
          ? String(response.isPublicAgency)
          : undefined,
      );
    }
  };

  const handlerSuppliersSelect = async (supplier: SupplierReduced) => {
    await handleEntitySelect(
      supplier,
      supplierMainAddress.mutateAsync,
      modalSupplierActions.close,
      'supplier',
    );
  };

  const handlerInsurerSelect = async (insurer: Insurer) => {
    await handleEntitySelect(
      insurer,
      insurerMainAddress.mutateAsync,
      modalInsurersActions.close,
      'insurer',
    );
  };

  function clearRecipient() {
    form?.resetFields([
      ['recipient', 'recipientType'],
      ['recipient', 'cpfCnpj'],
      ['recipient', 'foreignDocument'],
      ['recipient', 'supplier'],
      ['recipient', 'supplierId'],
      ['recipient', 'insurer'],
      ['recipient', 'insurerId'],
      ['recipient', 'customer'],
      ['recipient', 'customerId'],
      ['recipient', 'taxpayerType'],
      ['recipient', 'publicAgency'],
      ['taxpayerType'],
      ['finalConsumer'],
    ]);
  }

  return (
    <>
      <Form.Item noStyle name={['recipient', 'customerAddress']} hidden />
      <Form.Item noStyle name={['recipient', 'publicAgency']} hidden />
      <Form.Item noStyle name={['recipient', 'fantasyName']} hidden />
      <Form.Item noStyle name={['recipient', 'customerId']} hidden />
      <Form.Item noStyle name={['recipient', 'supplierId']} hidden />
      <Form.Item noStyle name={['recipient', 'insurerId']} hidden />
      <Form.Item noStyle name={['recipient', 'cityName']} hidden />
      <Form.Item noStyle name={['recipient', 'cpfCnpj']} hidden />
      <Form.Item noStyle name={['recipient', 'foreignDocument']} hidden />
      <Form.Item noStyle name="intermediaryIndication" hidden />

      <Col span={24} md={12} xl={6}>
        <FloatingFormItem
          initialValue={!isNFC ? RecipientType.customer : undefined}
          label={intl({ id: 'invoice.nfe.recipient.form.recipientType' })}
          name={['recipient', 'recipientType']}
          getValueFromEvent={(value) => {
            if (!value) {
              clearRecipient();
            } else {
              form?.resetFields(['recipient', 'cpfCnpj']);
            }

            return value;
          }}
        >
          <Select
            options={recipientTypeOptions}
            disabled={disabledFields}
            allowClear={isNFC || (isNfs && isLondrinaCity)}
          />
        </FloatingFormItem>
      </Col>

      {!!formValues?.recipient?.cpfCnpj && (
        <Col span={24} md={12} xl={6}>
          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.recipient?.recipientType !== curr.recipient?.recipientType
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const cpfCnpj = String(getFieldValue(['recipient', 'cpfCnpj']));

              return (
                <CpfCnpjField
                  cpfCnpj={cpfCnpj}
                  fieldName={['recipient', 'cpfCnpj']}
                  disabled
                />
              );
            }}
          </Form.Item>
        </Col>
      )}

      {!!formValues?.recipient?.foreignDocument && (
        <Col span={24} md={12} xl={6}>
          <FloatingFormItem
            name={['recipient', 'foreignDocument']}
            label="Documento estrangeiro"
          >
            <Input disabled />
          </FloatingFormItem>
        </Col>
      )}

      {(isNfs || invoiceType === InvoiceType.NFS) && (
        <Col span={24} md={12} xl={6}>
          <FloatingFormItem
            name={['recipient', 'publicAgency']}
            label="Orgão público"
            noStyle
          >
            <SelectYesNo allowClear disabled />
          </FloatingFormItem>
        </Col>
      )}

      <Col span={24} md={12} xl={6}>
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev.recipient?.recipientType !== curr.recipient?.recipientType
          }
        >
          {({ getFieldValue }) => {
            const recipientType = getFieldValue(['recipient', 'recipientType']);

            let name;
            let label;
            let inputSearchOnClick;

            if (recipientType === 'SUPPLIER') {
              name = ['recipient', 'supplier'];
              label = 'invoice.nfe.recipient.form.recipientType.supplier';
              inputSearchOnClick = modalSupplierActions;
            } else if (recipientType === 'INSURER') {
              name = ['recipient', 'insurer'];
              label = 'invoice.nfe.recipient.form.recipientType.insurer';
              inputSearchOnClick = modalInsurersActions;
            } else {
              name = ['recipient', 'customer'];
              label = 'invoice.nfe.recipient.form.recipientType.customer';
              inputSearchOnClick = modalCustomerActions;
            }

            return (
              <>
                <Col xs={24}>
                  <FloatingFormItem
                    label={intl({
                      id: label,
                    })}
                    name={name}
                    rules={
                      isLondrinaCity || isNFC ? [] : [defaultEmptyFieldError]
                    }
                  >
                    <InputSearch
                      buttonProps={{
                        onClick: inputSearchOnClick.toggle,
                        disabled: disabledFields || !recipientType,
                      }}
                      inputProps={{
                        disabled: true,
                      }}
                    />
                  </FloatingFormItem>
                </Col>
              </>
            );
          }}
        </Form.Item>
      </Col>

      <CustomersSelectedModal
        noSelection
        onCustomerSelect={handlerCustomerSelect}
        visible={modalCustomerVisible}
        handleVisible={modalCustomerActions.close}
      />

      <SuppliersSelectedModal
        noSelection
        onSupplierSelect={handlerSuppliersSelect}
        visible={modalSupplierVisible}
        handleVisible={modalSupplierActions.close}
      />

      <InsurersSelectedModal
        noSelection
        onInsurersSelect={handlerInsurerSelect}
        visible={modalInsurersVisible}
        handleVisible={modalInsurersActions.close}
      />
    </>
  );
};

export default SelectRecipientType;
